// ResumeTokenStore — per-(room, uid) resumeToken persistence in localStorage.
//
// Pairs with `room.welcome.resumeToken` (protocol-events.md §2.3) and the
// `room.resume` reconnect path (§2.7 / §2.8). Token + uid + permissions
// are minted by the server at welcome time and consumed on reconnect.
//
// Why a separate store from AdminTokenStore:
// - **Different lifetime**: admin_token outlives the WS session (used for
//   HTTP admin APIs in Phase 6). resumeToken expires fast (server side ~30
//   min, protocol-events.md §2.10 設計判断メモ); the client treats it as
//   short-lived and discards it on `room.rejected: 'resume-expired'`.
// - **Different keying**: resumeToken is keyed by **(roomId, uid)** because
//   one browser tab may have multiple uids over time (each connection
//   gets a fresh uid via room.welcome). Without uid in the key we'd
//   stomp the previous tab's token whenever a new tab joined. The tab
//   that owns the WS connection holds onto its own uid in memory and
//   uses it as the lookup key.
// - **Carries metadata**: expiresAt + permissions are stored alongside
//   the token so the resume hot path doesn't need a server round trip
//   to know whether the token is still good (expiresAt is the client's
//   guess based on welcome `ts` + a TTL hint; we re-validate on the
//   server side anyway, the client check just avoids a guaranteed-fail
//   resume attempt).
//
// localStorage probe + in-memory fallback follow the AdminTokenStore
// pattern (Safari private browsing / quota / SSR all degrade to in-memory).

const KEY_PREFIX = 'oekaki:resumeToken:';

const buildKey = (roomId: string, uid: string): string => `${KEY_PREFIX}${roomId}:${uid}`;

/**
 * Minimal Storage shape this module uses. `globalThis.localStorage` matches
 * this shape; tests can pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Persisted resume token plus the metadata needed to short-circuit a
 * doomed reconnect attempt without a network round trip.
 */
export interface StoredResumeToken {
  /** Server-issued token (opaque hex string, 32 bytes per protocol spec). */
  readonly token: string;
  /**
   * Wall-clock epoch ms when the client SHOULD treat the token as stale.
   * Set by the caller (typically `Date.now() + DEFAULT_TTL_MS`); the
   * server is the source of truth and may revoke earlier.
   */
  readonly expiresAt: number;
  /** Server-issued permissions bitflag at welcome time. */
  readonly permissions: number;
}

export interface ResumeTokenSaveInput {
  readonly roomId: string;
  readonly uid: string;
  readonly token: string;
  readonly expiresAt: number;
  readonly permissions: number;
}

export interface ResumeTokenLookup {
  readonly roomId: string;
  readonly uid: string;
}

export interface ResumeTokenStore {
  /**
   * Persist a token. Idempotent — repeated saves overwrite. No-op if
   * `token` is empty (defensive against a malformed welcome).
   */
  save(input: ResumeTokenSaveInput): void;
  /**
   * Look up a token. Returns `null` if missing or expired (expired
   * entries are purged as a side effect so the next `save` doesn't pile
   * up dead keys).
   */
  get(lookup: ResumeTokenLookup): StoredResumeToken | null;
  /** Remove the token. Idempotent. Used after `room.rejected: resume-expired`. */
  clear(lookup: ResumeTokenLookup): void;
}

/**
 * Probe `globalThis.localStorage` and return it if usable, else `null`.
 *
 * Mirrors AdminTokenStore's probe — Safari private browsing /
 * QuotaExceeded show up as exceptions on `setItem`. We test by writing
 * then deleting a sentinel key.
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = `${KEY_PREFIX}__probe__`;
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

const createMemoryStorage = (): StorageLike => {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => {
      map.set(k, v);
    },
    removeItem: (k) => {
      map.delete(k);
    },
  };
};

export interface CreateResumeTokenStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall back to in-memory if unavailable. Pass `null` to force fallback
   * (useful in tests that want a clean slate without touching the global).
   */
  storage?: StorageLike | null;
  /**
   * Source of `Date.now()` — overrideable for deterministic expiry tests.
   */
  now?: () => number;
}

const isStoredResumeToken = (raw: unknown): raw is StoredResumeToken => {
  if (typeof raw !== 'object' || raw === null) return false;
  const r = raw as Record<string, unknown>;
  return (
    typeof r.token === 'string' &&
    r.token.length > 0 &&
    typeof r.expiresAt === 'number' &&
    Number.isFinite(r.expiresAt) &&
    typeof r.permissions === 'number' &&
    Number.isFinite(r.permissions)
  );
};

export const createResumeTokenStore = (
  opts: CreateResumeTokenStoreOptions = {},
): ResumeTokenStore => {
  const explicit = opts.storage;
  const storage =
    explicit === null
      ? createMemoryStorage()
      : (explicit ?? probeBrowserStorage() ?? createMemoryStorage());
  const now = opts.now ?? (() => Date.now());

  const removeSafely = (key: string): void => {
    try {
      storage.removeItem(key);
    } catch {
      // ignore
    }
  };

  return {
    save(input) {
      if (input.token.length === 0) return;
      const value: StoredResumeToken = {
        token: input.token,
        expiresAt: input.expiresAt,
        permissions: input.permissions,
      };
      try {
        storage.setItem(buildKey(input.roomId, input.uid), JSON.stringify(value));
      } catch {
        // Quota / disabled storage. UX 上は再接続時に通常 join に fallback.
      }
    },
    get(lookup) {
      const key = buildKey(lookup.roomId, lookup.uid);
      let raw: string | null;
      try {
        raw = storage.getItem(key);
      } catch {
        return null;
      }
      if (raw === null) return null;
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        // Corrupt entry — purge so we don't keep failing.
        removeSafely(key);
        return null;
      }
      if (!isStoredResumeToken(parsed)) {
        removeSafely(key);
        return null;
      }
      if (parsed.expiresAt <= now()) {
        removeSafely(key);
        return null;
      }
      return parsed;
    },
    clear(lookup) {
      removeSafely(buildKey(lookup.roomId, lookup.uid));
    },
  };
};
